import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiX, FiPlus, FiTrash2, FiLoader, FiDownload, FiFileText, FiInfo, FiArrowLeft, FiArrowRight, FiUser, FiBriefcase, FiBookOpen, FiAward, FiLayout,
  FiCheck, FiZap, FiEdit3, FiRefreshCw, FiChevronUp, FiChevronDown, FiCamera } from "react-icons/fi";
import { API_BASE_URL, apiRequest, errorMessage } from "../api";

const STORAGE_KEY = "mraali_cv_builder_v1";
const MAX_PHOTO = 6 * 1024 * 1024;

const inputClass = "w-full px-3.5 py-2.5 rounded-lg bg-white/5 border border-navy-border text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-accent-light [color-scheme:dark]";
const labelClass = "block text-xs uppercase tracking-widest text-white/50 font-semibold mb-1.5";
const cardClass = "rounded-2xl bg-white/5 border border-navy-border p-5";
const ghostButton = "px-4 py-2 rounded-full border border-white/20 hover:border-accent-light text-sm font-medium inline-flex items-center gap-2 transition-colors disabled:opacity-50";

const BLANK = {
  experience: { title: "", company: "", location: "", start: "", end: "", current: false, description: "" },
  education: { degree: "", institution: "", location: "", start: "", end: "", grade: "", details: "" },
  languages: { name: "", level: "Fluent (C2)" },
  certifications: { name: "", issuer: "", year: "" },
  projects: { name: "", link: "", year: "", description: "" },
  awards: { title: "", issuer: "", year: "" },
  volunteer: { role: "", organization: "", start: "", end: "", current: false, description: "" },
  references: { name: "", position: "", company: "", phone: "", email: "" },
};

const emptyData = () => ({
  personal: {
    full_name: "", job_title: "", email: "", phone: "", phone2: "", address: "", city: "", state: "", country: "", postal_code: "", website: "", linkedin: "",
    dob: "", place_of_birth: "", nationality: "", gender: "", marital_status: "", father_name: "", cnic: "", religion: "", domicile: "",
    visa_status: "", driving_license: "", notice_period: "",
  },
  summary: "",
  experience: [{ ...BLANK.experience }],
  education: [{ ...BLANK.education }],
  skills: [],
  languages: [{ name: "English", level: "Fluent (C2)" }],
  certifications: [], projects: [], awards: [], publications: [], volunteer: [], references: [], interests: [],
  settings: { accent: "", references_on_request: true, declaration: true, show_photo: true },
});

// Anything restored from the browser or from a sample is merged into the standard shape, so a missing field never breaks the form.
function normalise(raw) {
  const base = emptyData();
  if (!raw || typeof raw !== "object") return base;
  const out = { ...base, ...raw, personal: { ...base.personal, ...(raw.personal || {}) }, settings: { ...base.settings, ...(raw.settings || {}) } };
  Object.keys(BLANK).forEach((key) => {
    out[key] = Array.isArray(raw[key]) ? raw[key].map((row) => ({ ...BLANK[key], ...row })) : base[key];
  });
  ["skills", "interests", "publications"].forEach((key) => { out[key] = Array.isArray(raw[key]) ? raw[key].map(String) : []; });
  out.summary = String(raw.summary || "");
  return out;
}

const STEPS = [
  { key: "personal", label: "About you", icon: FiUser },
  { key: "summary", label: "Summary", icon: FiEdit3 },
  { key: "experience", label: "Experience", icon: FiBriefcase },
  { key: "education", label: "Education", icon: FiBookOpen },
  { key: "skills", label: "Skills", icon: FiZap },
  { key: "more", label: "More", icon: FiAward },
  { key: "format", label: "Format and download", icon: FiLayout },
];

const GENDERS = ["", "Male", "Female", "Prefer not to say"];
const MARITAL = ["", "Single", "Married", "Divorced", "Widowed"];

function Field({ label, children, hint, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className={labelClass}>{label}</span>
      {children}
      {hint && <span className="block text-xs text-gray-500 mt-1">{hint}</span>}
    </label>
  );
}

function Text({ value, onChange, ...rest }) {
  return <input className={inputClass} value={value || ""} onChange={(e) => onChange(e.target.value)} {...rest} />;
}

function Select({ value, onChange, options }) {
  return (
    <select className={inputClass} value={value || ""} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => <option key={o} value={o}>{o || "Select..."}</option>)}
    </select>
  );
}

function Check({ checked, onChange, children }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-gray-300 cursor-pointer select-none">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="accent-[#22d3ee] w-4 h-4" />
      {children}
    </label>
  );
}

// Typing a skill and pressing Enter or comma adds it as a tag.
function TagInput({ values, onChange, placeholder }) {
  const [draft, setDraft] = useState("");
  const add = (text) => {
    const items = text.split(/[,\n;]+/).map((t) => t.trim()).filter(Boolean);
    if (!items.length) return;
    const next = [...values];
    items.forEach((t) => { if (!next.some((v) => v.toLowerCase() === t.toLowerCase())) next.push(t); });
    onChange(next);
    setDraft("");
  };
  return (
    <div className="rounded-lg bg-white/5 border border-navy-border p-2 focus-within:border-accent-light">
      <div className="flex flex-wrap gap-2">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent/15 border border-accent-light/30 text-accent-light text-xs">
            {v}
            <button type="button" aria-label={`Remove ${v}`} onClick={() => onChange(values.filter((x) => x !== v))} className="hover:text-white"><FiX size={12} /></button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => (e.target.value.includes(",") ? add(e.target.value) : setDraft(e.target.value))}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(draft); } else if (e.key === "Backspace" && !draft && values.length) onChange(values.slice(0, -1)); }}
          onBlur={() => add(draft)}
          placeholder={values.length ? "" : placeholder}
          className="flex-1 min-w-[160px] bg-transparent text-sm text-white placeholder:text-gray-500 px-2 py-1 focus:outline-none"
        />
      </div>
    </div>
  );
}

// A list of cards (jobs, degrees, certificates ...) with add, remove and move buttons.
function RowList({ title, rows, blank, onChange, render, addLabel, max = 12, empty }) {
  const update = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-4">
      {rows.length === 0 && empty && <p className="text-sm text-gray-500">{empty}</p>}
      {rows.map((row, i) => (
        <div key={i} className={cardClass}>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-gray-200">{title} {i + 1}</p>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30"><FiChevronUp /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === rows.length - 1} aria-label="Move down" className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30"><FiChevronDown /></button>
              <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} aria-label={`Remove ${title.toLowerCase()} ${i + 1}`} className="p-1.5 text-gray-400 hover:text-red-400"><FiTrash2 size={15} /></button>
            </div>
          </div>
          {render(row, (patch) => update(i, patch), i)}
        </div>
      ))}
      {rows.length < max && (
        <button type="button" onClick={() => onChange([...rows, { ...blank }])} className={ghostButton}><FiPlus size={15} /> {addLabel}</button>
      )}
    </div>
  );
}

function saveBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

const completeness = (data) => {
  const p = data.personal;
  const checks = [
    ["Your name", !!p.full_name.trim()], ["Job title", !!p.job_title.trim()], ["E-mail and phone", !!(p.email.trim() && p.phone.trim())], ["City and country", !!(p.city.trim() && p.country.trim())],
    ["Summary", data.summary.trim().length >= 60], ["Work experience", data.experience.some((e) => e.title.trim() && e.company.trim())],
    ["Achievements as bullet points", data.experience.some((e) => e.description.split("\n").filter(Boolean).length >= 2)], ["Education", data.education.some((e) => e.degree.trim())],
    ["At least 5 skills", data.skills.length >= 5], ["Languages", data.languages.some((l) => l.name.trim())],
  ];
  return { checks, percent: Math.round((checks.filter(([, ok]) => ok).length / checks.length) * 100) };
};

// AI Resume and CV Builder: collects what a CV needs, lays it out in a national or international format, and gives a Word file
// to edit and a PDF. A sample CV for any designation can be generated as a starting point.
export default function ResumeBuilderTool() {
  const [options, setOptions] = useState(null);
  const [data, setData] = useState(() => {
    try { return normalise(JSON.parse(localStorage.getItem(STORAGE_KEY) || "null")?.data); } catch { return emptyData(); }
  });
  const [format, setFormat] = useState(() => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null")?.format || "ats"; } catch { return "ats"; } });
  const [stage, setStage] = useState(() => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null")?.started ? "build" : "start"; } catch { return "start"; } });
  const [step, setStep] = useState(0);
  const [photo, setPhoto] = useState(null);              // { file, url }
  const [photoError, setPhotoError] = useState("");
  const [sample, setSample] = useState({ designation: "", level: "mid", country: "pakistan", name: "" });
  const [sampling, setSampling] = useState(false);
  const [sampleError, setSampleError] = useState("");
  const [notice, setNotice] = useState("");
  const [writing, setWriting] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const [preview, setPreview] = useState({ pages: [], page: 0, state: "idle", message: "" });
  const [busy, setBusy] = useState("");                  // "docx" | "pdf"
  const [error, setError] = useState("");
  const photoInput = useRef(null);
  const topRef = useRef(null);

  // --- loading and saving -----------------------------------------------------------------
  useEffect(() => {
    let alive = true;
    apiRequest("/core/resume/options/").then(({ ok, data: d }) => { if (alive && ok) setOptions(d); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ data, format, started: stage === "build" })); } catch { /* private mode: the CV simply is not remembered */ }
    }, 500);
    return () => clearTimeout(timer);
  }, [data, format, stage]);

  const set = useCallback((patch) => setData((d) => ({ ...d, ...patch })), []);
  const setPersonal = useCallback((key, value) => setData((d) => ({ ...d, personal: { ...d.personal, [key]: value } })), []);
  const setSetting = useCallback((key, value) => setData((d) => ({ ...d, settings: { ...d.settings, [key]: value } })), []);

  const formats = options?.formats || [];
  const current = formats.find((f) => f.key === format) || formats[0];
  const goTo = (i) => { setStep(i); setTimeout(() => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 30); };

  // --- the sample generator -----------------------------------------------------------------
  const generateSample = async () => {
    setSampleError("");
    setSampling(true);
    const { ok, data: d } = await apiRequest("/core/resume/sample/", { method: "POST", body: sample });
    setSampling(false);
    if (!ok) return setSampleError(d?.designation || errorMessage(d));
    setData(normalise(d.data));
    setFormat(d.suggested_format || "modern");
    setNotice(`${d.notice ? `${d.notice} ` : ""}This is a sample - replace "Your Name" and the other placeholder details, and edit the achievements so they describe you.`);
    setStage("build");
    goTo(6);
  };

  const startBlank = () => { setStage("build"); goTo(0); };
  const startOver = () => {
    if (!window.confirm("Clear everything you entered and start again?")) return;
    setData(emptyData());
    setPhoto(null);
    setNotice("");
    setStage("start");
    setStep(0);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* nothing to remove */ }
  };

  // --- the summary writer ---------------------------------------------------------------------
  const writeSummary = async () => {
    setSummaryError("");
    setWriting(true);
    const { ok, data: d } = await apiRequest("/core/resume/summary/", { method: "POST", body: { data } });
    setWriting(false);
    if (!ok) return setSummaryError(d?.detail || errorMessage(d));
    set({ summary: d.summary });
  };

  // --- the photo ---------------------------------------------------------------------------------
  const pickPhoto = (file) => {
    setPhotoError("");
    if (!file) return;
    if (!file.type.startsWith("image/")) return setPhotoError("Choose a JPG or PNG picture.");
    if (file.size > MAX_PHOTO) return setPhotoError("The photo is larger than 6 MB.");
    if (photo?.url) URL.revokeObjectURL(photo.url);
    setPhoto({ file, url: URL.createObjectURL(file) });
  };

  // --- building the files ------------------------------------------------------------------------
  const request = useCallback(async (kind) => {
    const form = new FormData();
    form.append("data", JSON.stringify(data));
    form.append("format", format);
    form.append("kind", kind);
    if (photo?.file) form.append("photo", photo.file);
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/core/resume/build/`, { method: "POST", body: form });
    } catch {
      return { ok: false, message: "Could not reach the server." };
    }
    if (!response.ok) {
      let body = null;
      try { body = await response.json(); } catch { /* not JSON */ }
      return { ok: false, code: body?.code, message: body?.detail || errorMessage(body, "The file could not be made. Please try again.") };
    }
    const disposition = response.headers.get("Content-Disposition") || "";
    return { ok: true, blob: await response.blob(), filename: (disposition.match(/filename="([^"]+)"/) || [])[1] };
  }, [data, format, photo]);

  const download = async (kind) => {
    setError("");
    if (!data.personal.full_name.trim()) { setError("Please enter your full name first."); return goTo(0); }
    setBusy(kind);
    const result = await request(kind);
    setBusy("");
    if (!result.ok) return setError(result.message);
    saveBlob(result.filename || `CV.${kind}`, result.blob);
  };

  // The preview is the real layout of the PDF as pictures, drawn again a moment after the last change.
  const onFormatStep = stage === "build" && step === 6;
  useEffect(() => {
    if (!onFormatStep) return undefined;
    if (!data.personal.full_name.trim()) { setPreview((p) => ({ ...p, state: "empty" })); return undefined; }
    let alive = true;
    setPreview((p) => ({ ...p, state: "loading" }));
    const timer = setTimeout(async () => {
      const form = new FormData();
      form.append("data", JSON.stringify(data));
      form.append("format", format);
      form.append("kind", "preview");
      if (photo?.file) form.append("photo", photo.file);
      const { ok, data: body } = await apiRequest("/core/resume/build/", { method: "POST", body: form });
      if (!alive) return;
      if (!ok) return setPreview((p) => ({ ...p, state: body?.code === "pdf_unsupported" ? "unsupported" : "error", message: body?.detail || errorMessage(body) }));
      setPreview((p) => ({ pages: body.pages || [], page: Math.min(p.page, (body.pages || []).length - 1), state: "ready", message: body.page_count > (body.pages || []).length ? `Showing the first ${body.pages.length} of ${body.page_count} pages.` : "" }));
    }, 800);
    return () => { alive = false; clearTimeout(timer); };
  }, [onFormatStep, data, format, photo]);

  const strength = useMemo(() => completeness(data), [data]);

  // =====================================================================================================
  // The start screen
  // =====================================================================================================
  if (stage === "start") {
    return (
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-14">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Resume and CV Builder</h2>
        <p className="text-center text-gray-400 max-w-2xl mx-auto mb-10">
          Make a CV that fits the country you are applying in. Fill in your details step by step, choose a national or international format,
          and download it as Word (to edit) or PDF. In a hurry? Ask for a ready-made sample for any designation.
        </p>
        <div className="grid md:grid-cols-2 gap-6">
          <div className={`${cardClass} flex flex-col`}>
            <FiEdit3 className="text-accent-light mb-3" size={26} />
            <h3 className="text-lg font-bold mb-2">Build my own CV</h3>
            <p className="text-sm text-gray-400 mb-5 flex-1">Answer a few questions about you, your jobs, education and skills. Your work is saved in this browser while you go.</p>
            <button onClick={startBlank} className="px-6 py-3 rounded-full bg-brand-gradient text-white font-semibold inline-flex items-center justify-center gap-2 shadow-lg shadow-accent/20">
              Start my CV <FiArrowRight />
            </button>
            {(data.personal.full_name || data.experience.some((e) => e.title)) && (
              <button onClick={() => setStage("build")} className="mt-3 text-sm text-accent-light hover:underline">Continue where I left off</button>
            )}
          </div>

          <div className={cardClass}>
            <FiZap className="text-accent-light mb-3" size={26} />
            <h3 className="text-lg font-bold mb-2">Generate a sample for a designation</h3>
            <p className="text-sm text-gray-400 mb-4">Type a job such as accountant, teacher, IT expert or marketing. You get a complete CV to edit.</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {(options?.designations || []).slice(0, 8).map((d) => (
                <button key={d} type="button" onClick={() => setSample((s) => ({ ...s, designation: d }))}
                  className={`px-3 py-1 rounded-full text-xs border transition-colors ${sample.designation === d ? "bg-accent-light text-navy border-accent-light" : "border-navy-border text-gray-300 hover:border-accent-light"}`}>{d}</button>
              ))}
            </div>
            <div className="space-y-3">
              <Field label="Designation"><Text value={sample.designation} onChange={(v) => setSample((s) => ({ ...s, designation: v }))} placeholder="e.g. Accountant, Teacher, IT Expert, Marketing Manager" maxLength={80} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Experience">
                  <select className={inputClass} value={sample.level} onChange={(e) => setSample((s) => ({ ...s, level: e.target.value }))}>
                    {(options?.levels || []).map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                  </select>
                </Field>
                <Field label="Applying in">
                  <select className={inputClass} value={sample.country} onChange={(e) => setSample((s) => ({ ...s, country: e.target.value }))}>
                    {(options?.countries || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Your name (optional)"><Text value={sample.name} onChange={(v) => setSample((s) => ({ ...s, name: v }))} placeholder="Leave empty to use 'Your Name'" maxLength={80} /></Field>
            </div>
            {sampleError && <p className="text-sm text-amber-300 mt-3">{sampleError}</p>}
            <button onClick={generateSample} disabled={sampling || sample.designation.trim().length < 2}
              className="w-full mt-4 px-6 py-3 rounded-full bg-brand-gradient text-white font-semibold inline-flex items-center justify-center gap-2 shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed">
              {sampling ? <><FiLoader className="animate-spin" /> Writing the sample...</> : <><FiZap /> Generate sample CV</>}
            </button>
          </div>
        </div>

        <div className="mt-10">
          <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4 text-center">CV formats you can choose</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {formats.map((f) => (
              <div key={f.key} className="rounded-xl bg-white/5 border border-navy-border p-4">
                <span className="inline-block w-6 h-1.5 rounded-full mb-3" style={{ background: `#${f.accent}` }} />
                <p className="text-sm font-semibold">{f.label}</p>
                <p className="text-xs text-accent-light mt-0.5">{f.region}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // =====================================================================================================
  // The builder
  // =====================================================================================================
  const p = data.personal;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-14" ref={topRef}>
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Resume and CV Builder</h2>
      <p className="text-center text-gray-400 max-w-2xl mx-auto mb-8">Fill in the steps in any order. Everything is saved in this browser. Nothing is sent to anyone until you download.</p>

      {notice && (
        <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-100 text-sm px-5 py-4 mb-6 flex items-start gap-3">
          <FiInfo className="mt-0.5 shrink-0" /><span>{notice}</span>
          <button type="button" onClick={() => setNotice("")} aria-label="Dismiss" className="ms-auto text-amber-200 hover:text-white"><FiX /></button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex flex-wrap gap-2">
          {STEPS.map((s, i) => (
            <button key={s.key} type="button" onClick={() => goTo(i)}
              className={`px-3.5 py-2 rounded-full text-sm font-medium inline-flex items-center gap-2 transition-colors ${step === i ? "bg-brand-gradient text-white shadow-md shadow-accent/20" : "bg-white/5 border border-navy-border text-gray-300 hover:border-accent-light"}`}>
              <s.icon size={14} /> <span className="hidden sm:inline">{s.label}</span><span className="sm:hidden">{i + 1}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span title={strength.checks.map(([l, ok]) => `${ok ? "✓" : "○"} ${l}`).join("\n")}>CV completeness {strength.percent}%</span>
          <div className="w-24 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-accent-light transition-all" style={{ width: `${strength.percent}%` }} /></div>
          <button type="button" onClick={startOver} className="text-gray-400 hover:text-red-400 inline-flex items-center gap-1"><FiRefreshCw size={12} /> Start over</button>
        </div>
      </div>

      {/* ------------------------------------------------------------------ About you */}
      {step === 0 && (
        <div className="space-y-5">
          <div className={cardClass}>
            <p className="font-semibold mb-4">Name and contact</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name *"><Text value={p.full_name} onChange={(v) => setPersonal("full_name", v)} placeholder="e.g. Ali Raza Khan" /></Field>
              <Field label="Job title / target role"><Text value={p.job_title} onChange={(v) => setPersonal("job_title", v)} placeholder="e.g. Senior Accountant" /></Field>
              <Field label="E-mail"><Text type="email" value={p.email} onChange={(v) => setPersonal("email", v)} placeholder="name@email.com" /></Field>
              <Field label="Phone / WhatsApp"><Text value={p.phone} onChange={(v) => setPersonal("phone", v)} placeholder="+92 300 1234567" /></Field>
              <Field label="Second phone (optional)"><Text value={p.phone2} onChange={(v) => setPersonal("phone2", v)} /></Field>
              <Field label="Street address (optional)"><Text value={p.address} onChange={(v) => setPersonal("address", v)} /></Field>
              <Field label="City"><Text value={p.city} onChange={(v) => setPersonal("city", v)} placeholder="Lahore" /></Field>
              <Field label="State / province (optional)"><Text value={p.state} onChange={(v) => setPersonal("state", v)} /></Field>
              <Field label="Country"><Text value={p.country} onChange={(v) => setPersonal("country", v)} placeholder="Pakistan" /></Field>
              <Field label="Postal code (optional)"><Text value={p.postal_code} onChange={(v) => setPersonal("postal_code", v)} /></Field>
              <Field label="LinkedIn (optional)"><Text value={p.linkedin} onChange={(v) => setPersonal("linkedin", v)} placeholder="linkedin.com/in/yourname" /></Field>
              <Field label="Website / portfolio (optional)"><Text value={p.website} onChange={(v) => setPersonal("website", v)} placeholder="yourname.com" /></Field>
            </div>
          </div>

          <div className={cardClass}>
            <p className="font-semibold mb-1">Photo (optional)</p>
            <p className="text-xs text-gray-500 mb-4">Used by the formats that carry a photo (Pakistan / India, Gulf, German, Europass, Modern). US, UK and Academic CVs leave it out automatically.</p>
            <div className="flex items-center gap-4">
              {photo ? <img src={photo.url} alt="Selected portrait" className="w-20 h-24 object-cover rounded-lg border border-navy-border" /> : <div className="w-20 h-24 rounded-lg border border-dashed border-navy-border flex items-center justify-center text-gray-500"><FiCamera size={22} /></div>}
              <div className="space-y-2">
                <input ref={photoInput} type="file" accept="image/*" className="hidden" onChange={(e) => { pickPhoto(e.target.files?.[0]); e.target.value = ""; }} />
                <button type="button" onClick={() => photoInput.current?.click()} className={ghostButton}><FiCamera size={15} /> {photo ? "Change photo" : "Add a photo"}</button>
                {photo && <button type="button" onClick={() => { URL.revokeObjectURL(photo.url); setPhoto(null); }} className="block text-xs text-gray-400 hover:text-red-400">Remove photo</button>}
                {photoError && <p className="text-xs text-amber-300">{photoError}</p>}
                <p className="text-xs text-gray-500">Not saved on our server - it is only used to make your file.</p>
              </div>
            </div>
          </div>

          <div className={cardClass}>
            <p className="font-semibold mb-1">Personal details</p>
            <p className="text-xs text-gray-500 mb-4">
              Only the formats that expect them show these (Pakistan / India, Gulf, German, Europass). A US or UK CV leaves them out, so fill in what applies to you
              {current ? ` - the selected format, ${current.label}, uses: ${current.personal_fields.length ? current.personal_fields.map((f) => f.replace(/_/g, " ")).join(", ") : "none of them"}.` : "."}
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Date of birth"><Text type="date" value={p.dob} onChange={(v) => setPersonal("dob", v)} /></Field>
              <Field label="Place of birth"><Text value={p.place_of_birth} onChange={(v) => setPersonal("place_of_birth", v)} /></Field>
              <Field label="Nationality"><Text value={p.nationality} onChange={(v) => setPersonal("nationality", v)} placeholder="Pakistani" /></Field>
              <Field label="Gender"><Select value={p.gender} onChange={(v) => setPersonal("gender", v)} options={GENDERS} /></Field>
              <Field label="Marital status"><Select value={p.marital_status} onChange={(v) => setPersonal("marital_status", v)} options={MARITAL} /></Field>
              <Field label="Father's name"><Text value={p.father_name} onChange={(v) => setPersonal("father_name", v)} /></Field>
              <Field label="National ID / CNIC (optional)"><Text value={p.cnic} onChange={(v) => setPersonal("cnic", v)} /></Field>
              <Field label="Domicile (optional)"><Text value={p.domicile} onChange={(v) => setPersonal("domicile", v)} /></Field>
              <Field label="Religion (optional)"><Text value={p.religion} onChange={(v) => setPersonal("religion", v)} /></Field>
              <Field label="Visa status"><Text value={p.visa_status} onChange={(v) => setPersonal("visa_status", v)} placeholder="e.g. Visit visa, Employment visa" /></Field>
              <Field label="Driving licence"><Text value={p.driving_license} onChange={(v) => setPersonal("driving_license", v)} placeholder="e.g. Yes - UAE licence" /></Field>
              <Field label="Notice period"><Text value={p.notice_period} onChange={(v) => setPersonal("notice_period", v)} placeholder="e.g. 30 days / Immediate" /></Field>
            </div>
            {(p.cnic || p.dob) && <p className="text-xs text-gray-500 mt-3">Think before sharing your ID number: add it only if the employer asks for it.</p>}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ Summary */}
      {step === 1 && (
        <div className={cardClass}>
          <p className="font-semibold mb-1">Professional summary / career objective</p>
          <p className="text-xs text-gray-500 mb-4">Two or three sentences: who you are, what you are good at, and what you want. Pakistan / India CVs call it the career objective.</p>
          <textarea className={`${inputClass} min-h-[140px] leading-relaxed`} value={data.summary} onChange={(e) => set({ summary: e.target.value.slice(0, 1500) })}
            placeholder="e.g. Detail-oriented accountant with 8 years of experience in audit, tax and financial reporting..." />
          <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
            <span className="text-xs text-gray-500">{data.summary.length} / 1500 characters</span>
            <button type="button" onClick={writeSummary} disabled={writing} className={ghostButton}>
              {writing ? <><FiLoader className="animate-spin" /> Writing...</> : <><FiZap size={14} /> Write it for me from my details</>}
            </button>
          </div>
          {summaryError && <p className="text-sm text-amber-300 mt-2">{summaryError}</p>}
        </div>
      )}

      {/* ------------------------------------------------------------------ Experience */}
      {step === 2 && (
        <div>
          <p className="text-sm text-gray-400 mb-4">Start with your most recent job. Write achievements, one per line (a result with a number is best: "Cut reporting time by 30%").</p>
          <RowList title="Job" rows={data.experience} blank={BLANK.experience} onChange={(rows) => set({ experience: rows })} addLabel="Add another job" max={15}
            empty="No jobs yet. A fresh graduate can add an internship, or skip this step."
            render={(row, update) => (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Job title"><Text value={row.title} onChange={(v) => update({ title: v })} placeholder="Senior Accountant" /></Field>
                <Field label="Company"><Text value={row.company} onChange={(v) => update({ company: v })} /></Field>
                <Field label="City / country"><Text value={row.location} onChange={(v) => update({ location: v })} /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="From"><Text type="month" value={row.start} onChange={(v) => update({ start: v })} /></Field>
                  <Field label="To">{row.current ? <p className="text-sm text-gray-400 py-2.5">Present</p> : <Text type="month" value={row.end} onChange={(v) => update({ end: v })} />}</Field>
                </div>
                <div className="sm:col-span-2"><Check checked={row.current} onChange={(v) => update({ current: v })}>I work here now</Check></div>
                <Field label="Responsibilities and achievements (one per line)" className="sm:col-span-2">
                  <textarea className={`${inputClass} min-h-[110px] leading-relaxed`} value={row.description} onChange={(e) => update({ description: e.target.value })}
                    placeholder={"Led the monthly close for three business units\nCut reporting time by 30%"} />
                </Field>
              </div>
            )} />
        </div>
      )}

      {/* ------------------------------------------------------------------ Education */}
      {step === 3 && (
        <div>
          <p className="text-sm text-gray-400 mb-4">Latest first. Add school-level qualifications too if you are applying for your first job or in Pakistan / India.</p>
          <RowList title="Qualification" rows={data.education} blank={BLANK.education} onChange={(rows) => set({ education: rows })} addLabel="Add another qualification" max={10}
            render={(row, update) => (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Degree / qualification"><Text value={row.degree} onChange={(v) => update({ degree: v })} placeholder="BCom (Hons) Accounting" /></Field>
                <Field label="School / university / board"><Text value={row.institution} onChange={(v) => update({ institution: v })} /></Field>
                <Field label="City / country"><Text value={row.location} onChange={(v) => update({ location: v })} /></Field>
                <Field label="Grade / CGPA / percentage"><Text value={row.grade} onChange={(v) => update({ grade: v })} placeholder="3.4 / 4.0" /></Field>
                <Field label="Started"><Text type="month" value={row.start} onChange={(v) => update({ start: v })} /></Field>
                <Field label="Finished (or expected)"><Text type="month" value={row.end} onChange={(v) => update({ end: v })} /></Field>
                <Field label="Details (optional): thesis, honours, relevant courses" className="sm:col-span-2"><Text value={row.details} onChange={(v) => update({ details: v })} /></Field>
              </div>
            )} />
        </div>
      )}

      {/* ------------------------------------------------------------------ Skills */}
      {step === 4 && (
        <div className="space-y-5">
          <div className={cardClass}>
            <p className="font-semibold mb-1">Skills</p>
            <p className="text-xs text-gray-500 mb-3">Type a skill and press Enter or comma. Use the words from the job advert: recruiting software matches them.</p>
            <TagInput values={data.skills} onChange={(skills) => set({ skills })} placeholder="e.g. IFRS, Excel, QuickBooks, Public speaking..." />
            {data.skills.length > 0 && data.skills.length < 5 && <p className="text-xs text-amber-300 mt-2">Add at least 5 skills for a stronger CV.</p>}
          </div>
          <div className={cardClass}>
            <p className="font-semibold mb-3">Languages</p>
            <div className="space-y-3">
              {data.languages.map((l, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end">
                  <Field label={i === 0 ? "Language" : ""}><Text value={l.name} onChange={(v) => set({ languages: data.languages.map((x, j) => (j === i ? { ...x, name: v } : x)) })} placeholder="English" /></Field>
                  <Field label={i === 0 ? "Level" : ""}>
                    <select className={inputClass} value={l.level} onChange={(e) => set({ languages: data.languages.map((x, j) => (j === i ? { ...x, level: e.target.value } : x)) })}>
                      {(options?.language_levels || ["Native", "Fluent (C2)", "Advanced (C1)", "Intermediate (B1)", "Basic (A2)"]).map((v) => <option key={v}>{v}</option>)}
                    </select>
                  </Field>
                  <button type="button" onClick={() => set({ languages: data.languages.filter((_, j) => j !== i) })} aria-label="Remove language" className="p-2.5 text-gray-400 hover:text-red-400"><FiTrash2 size={15} /></button>
                </div>
              ))}
              {data.languages.length < 12 && <button type="button" onClick={() => set({ languages: [...data.languages, { ...BLANK.languages }] })} className={ghostButton}><FiPlus size={15} /> Add a language</button>}
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ More */}
      {step === 5 && (
        <div className="space-y-8">
          <section>
            <p className="font-semibold mb-3">Certifications and trainings</p>
            <RowList title="Certificate" rows={data.certifications} blank={BLANK.certifications} onChange={(rows) => set({ certifications: rows })} addLabel="Add a certificate" max={20}
              empty="Courses, licences and trainings you completed."
              render={(row, update) => (
                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label="Name"><Text value={row.name} onChange={(v) => update({ name: v })} /></Field>
                  <Field label="Issued by"><Text value={row.issuer} onChange={(v) => update({ issuer: v })} /></Field>
                  <Field label="Year"><Text value={row.year} onChange={(v) => update({ year: v })} placeholder="2023" /></Field>
                </div>
              )} />
          </section>
          <section>
            <p className="font-semibold mb-3">Projects</p>
            <RowList title="Project" rows={data.projects} blank={BLANK.projects} onChange={(rows) => set({ projects: rows })} addLabel="Add a project" max={10}
              render={(row, update) => (
                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label="Project name"><Text value={row.name} onChange={(v) => update({ name: v })} /></Field>
                  <Field label="Link (optional)"><Text value={row.link} onChange={(v) => update({ link: v })} /></Field>
                  <Field label="Year"><Text value={row.year} onChange={(v) => update({ year: v })} /></Field>
                  <Field label="What it was and what you did" className="sm:col-span-3"><Text value={row.description} onChange={(v) => update({ description: v })} /></Field>
                </div>
              )} />
          </section>
          <section>
            <p className="font-semibold mb-3">Awards and honours</p>
            <RowList title="Award" rows={data.awards} blank={BLANK.awards} onChange={(rows) => set({ awards: rows })} addLabel="Add an award" max={15}
              render={(row, update) => (
                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label="Award"><Text value={row.title} onChange={(v) => update({ title: v })} /></Field>
                  <Field label="Given by"><Text value={row.issuer} onChange={(v) => update({ issuer: v })} /></Field>
                  <Field label="Year"><Text value={row.year} onChange={(v) => update({ year: v })} /></Field>
                </div>
              )} />
          </section>
          <section>
            <p className="font-semibold mb-3">Volunteer work</p>
            <RowList title="Role" rows={data.volunteer} blank={BLANK.volunteer} onChange={(rows) => set({ volunteer: rows })} addLabel="Add volunteer work" max={8}
              render={(row, update) => (
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Role"><Text value={row.role} onChange={(v) => update({ role: v })} /></Field>
                  <Field label="Organisation"><Text value={row.organization} onChange={(v) => update({ organization: v })} /></Field>
                  <Field label="From"><Text type="month" value={row.start} onChange={(v) => update({ start: v })} /></Field>
                  <Field label="To">{row.current ? <p className="text-sm text-gray-400 py-2.5">Present</p> : <Text type="month" value={row.end} onChange={(v) => update({ end: v })} />}</Field>
                  <div className="sm:col-span-2"><Check checked={row.current} onChange={(v) => update({ current: v })}>I still do this</Check></div>
                  <Field label="What you did (one per line)" className="sm:col-span-2"><textarea className={`${inputClass} min-h-[80px]`} value={row.description} onChange={(e) => update({ description: e.target.value })} /></Field>
                </div>
              )} />
          </section>
          <section className={cardClass}>
            <p className="font-semibold mb-3">Publications (academic CVs)</p>
            <textarea className={`${inputClass} min-h-[90px]`} value={data.publications.join("\n")} onChange={(e) => set({ publications: e.target.value.split("\n") })}
              placeholder={"One publication per line, e.g. Khan, A. (2024). Title of the paper. Journal, 12(3), 45-60."} />
          </section>
          <section className={cardClass}>
            <p className="font-semibold mb-3">Interests / hobbies</p>
            <TagInput values={data.interests} onChange={(interests) => set({ interests })} placeholder="e.g. Cricket, Reading, Volunteering" />
          </section>
          <section>
            <p className="font-semibold mb-3">References</p>
            <RowList title="Referee" rows={data.references} blank={BLANK.references} onChange={(rows) => set({ references: rows })} addLabel="Add a referee" max={4}
              empty="Most CVs simply say 'References available on request'. Add referees only if the employer asks."
              render={(row, update) => (
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Name"><Text value={row.name} onChange={(v) => update({ name: v })} /></Field>
                  <Field label="Position"><Text value={row.position} onChange={(v) => update({ position: v })} /></Field>
                  <Field label="Company"><Text value={row.company} onChange={(v) => update({ company: v })} /></Field>
                  <Field label="Phone"><Text value={row.phone} onChange={(v) => update({ phone: v })} /></Field>
                  <Field label="E-mail" className="sm:col-span-2"><Text value={row.email} onChange={(v) => update({ email: v })} /></Field>
                </div>
              )} />
            <div className="flex flex-col gap-2 mt-4">
              <Check checked={data.settings.references_on_request} onChange={(v) => setSetting("references_on_request", v)}>Write "References available on request" when I list none</Check>
              <Check checked={data.settings.declaration} onChange={(v) => setSetting("declaration", v)}>Add the declaration and signature line (Pakistan / India CVs)</Check>
            </div>
          </section>
        </div>
      )}

      {/* ------------------------------------------------------------------ Format and download */}
      {step === 6 && (
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6">
          <div className="space-y-5">
            <div className={cardClass}>
              <p className="font-semibold mb-1">Choose the CV format</p>
              <p className="text-xs text-gray-500 mb-4">Pick the style the employer in that country expects. You can switch at any time: your details stay the same.</p>
              <div className="space-y-2.5">
                {formats.map((f) => (
                  <button key={f.key} type="button" onClick={() => setFormat(f.key)} aria-pressed={format === f.key}
                    className={`w-full text-start rounded-xl border p-3.5 transition-colors ${format === f.key ? "border-accent-light bg-accent/10" : "border-navy-border bg-white/5 hover:border-accent-light/60"}`}>
                    <div className="flex items-center gap-3">
                      <span className="w-2.5 h-9 rounded-full shrink-0" style={{ background: `#${f.accent}` }} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold flex items-center gap-2">{f.label}{format === f.key && <FiCheck className="text-accent-light" size={14} />}</p>
                        <p className="text-xs text-accent-light">{f.region}</p>
                      </div>
                      <span className="text-[11px] text-gray-400 shrink-0">{f.photo === "never" ? "No photo" : f.photo === "recommended" ? "Photo" : "Photo optional"}</span>
                    </div>
                    {format === f.key && <p className="text-xs text-gray-400 mt-2.5 leading-relaxed">{f.description} <span className="text-gray-500">Length: {f.pages}.</span></p>}
                  </button>
                ))}
              </div>
            </div>

            <div className={cardClass}>
              <p className="font-semibold mb-3">Colour</p>
              <div className="flex flex-wrap gap-2.5">
                <button type="button" onClick={() => setSetting("accent", "")} aria-pressed={!data.settings.accent}
                  className={`px-3 h-9 rounded-full text-xs border ${!data.settings.accent ? "border-accent-light text-accent-light" : "border-navy-border text-gray-400"}`}>Format default</button>
                {(options?.accents || []).map((c) => (
                  <button key={c} type="button" aria-label={`Colour #${c}`} aria-pressed={data.settings.accent === c} onClick={() => setSetting("accent", c)}
                    className={`w-9 h-9 rounded-full border-2 ${data.settings.accent === c ? "border-white" : "border-transparent"}`} style={{ background: `#${c}` }} />
                ))}
              </div>
              {current && current.photo !== "never" && (
                <div className="mt-4 pt-4 border-t border-navy-border flex flex-wrap items-center gap-4">
                  <Check checked={data.settings.show_photo} onChange={(v) => setSetting("show_photo", v)}>Show my photo</Check>
                  {!photo && <button type="button" onClick={() => goTo(0)} className="text-xs text-accent-light hover:underline">Add a photo</button>}
                </div>
              )}
            </div>

            <div className={cardClass}>
              <p className="font-semibold mb-1">Download</p>
              <p className="text-xs text-gray-500 mb-4">Word opens in Word, Google Docs and LibreOffice: change anything, then save. The PDF is ready to send.</p>
              {error && <p className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 mb-4">{error}</p>}
              <div className="flex flex-wrap gap-3">
                <button type="button" onClick={() => download("docx")} disabled={!!busy}
                  className="px-5 py-3 rounded-full bg-brand-gradient text-white font-semibold text-sm inline-flex items-center gap-2 shadow-lg shadow-accent/20 disabled:opacity-60">
                  {busy === "docx" ? <FiLoader className="animate-spin" /> : <FiDownload />} Word (.docx) - to edit
                </button>
                <button type="button" onClick={() => download("pdf")} disabled={!!busy || preview.state === "unsupported"} className={`${ghostButton} px-5 py-3`}>
                  {busy === "pdf" ? <FiLoader className="animate-spin" /> : <FiFileText />} PDF
                </button>
              </div>
              {preview.state === "unsupported" && <p className="text-xs text-amber-300 mt-3">{preview.message}</p>}
            </div>
          </div>

          <div className="lg:sticky lg:top-24 self-start">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-widest text-white/50 font-semibold">Preview</p>
              {preview.pages.length > 1 && (
                <div className="flex items-center gap-1.5">
                  {preview.pages.map((_, i) => (
                    <button key={i} type="button" onClick={() => setPreview((p) => ({ ...p, page: i }))} aria-label={`Page ${i + 1}`} aria-pressed={preview.page === i}
                      className={`w-7 h-7 rounded-full text-xs font-semibold border ${preview.page === i ? "bg-accent-light text-navy border-accent-light" : "border-navy-border text-gray-300 hover:border-accent-light"}`}>{i + 1}</button>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-xl bg-white border border-navy-border overflow-hidden relative" style={{ aspectRatio: "1 / 1.414" }}>
              {preview.pages[preview.page] && <img src={preview.pages[preview.page]} alt={`CV preview, page ${preview.page + 1}`} className={`absolute inset-0 w-full h-full object-contain transition-opacity ${preview.state === "loading" ? "opacity-50" : ""}`} />}
              {(preview.state !== "ready" || !preview.pages.length) && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 text-gray-500 text-sm gap-3 bg-navy/60">
                  {preview.state === "loading" && <><FiLoader className="animate-spin text-accent-light" size={22} /> Drawing your CV...</>}
                  {preview.state === "empty" && <>Enter your name in the first step to see the preview.</>}
                  {preview.state === "unsupported" && <>The preview needs Latin letters. Download the Word file instead.</>}
                  {preview.state === "error" && <>{preview.message || "The preview could not be made."}</>}
                  {preview.state === "idle" && <>The preview appears here.</>}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">{preview.message ? `${preview.message} ` : ""}This is how the PDF looks. The Word file has the same layout, in the format's own font, and every part can be edited.</p>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ navigation */}
      <div className="flex items-center justify-between mt-8">
        <button type="button" onClick={() => goTo(Math.max(0, step - 1))} disabled={step === 0} className={ghostButton}><FiArrowLeft size={15} /> Back</button>
        {step < STEPS.length - 1 ? (
          <button type="button" onClick={() => goTo(step + 1)} className="px-6 py-2.5 rounded-full bg-brand-gradient text-white text-sm font-semibold inline-flex items-center gap-2 shadow-md shadow-accent/20">
            Next: {STEPS[step + 1].label} <FiArrowRight size={15} />
          </button>
        ) : (
          <button type="button" onClick={() => goTo(0)} className={ghostButton}><FiEdit3 size={15} /> Edit my details</button>
        )}
      </div>
      <p className="text-xs text-gray-500 text-center mt-8 max-w-2xl mx-auto">
        Your details stay in this browser and are only sent to make the file you ask for. Sample content is placeholder text: always replace it with your own facts before you apply.
      </p>
    </div>
  );
}
